import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { CheckCircle, Loader2, XCircle, Sparkles, BookOpen, Trophy, Zap } from "lucide-react";
import { Button } from "../components/ui/button";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

function ConfettiParticle({ delay, left }) {
  const colors = ["#F7931A", "#4ade80", "#fbbf24", "#60a5fa", "#f472b6"];
  const color = colors[Math.floor(Math.random() * colors.length)];
  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full"
      style={{ backgroundColor: color, left: `${left}%`, top: -10 }}
      initial={{ opacity: 1, y: 0, rotate: 0 }}
      animate={{ opacity: 0, y: 600, rotate: 720, x: (Math.random() - 0.5) * 200 }}
      transition={{ duration: 2.5, delay, ease: "easeOut" }}
    />
  );
}

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const [tierName, setTierName] = useState("");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await api.get(`/payments/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === "paid") {
          setStatus("success");
          setTierName(res.data.tier || "");
          await refreshUser();
          toast.success("Welcome to Bitcoin Brain!");
        } else if (res.data.status === "expired") {
          setStatus("error");
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      } catch (error) {
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkStatus();
  }, [searchParams, attempts, refreshUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 overflow-hidden relative">
      {status === "success" && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 40 }).map((_, i) => (
            <ConfettiParticle key={i} delay={i * 0.05} left={Math.random() * 100} />
          ))}
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-lg relative z-10"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-[#F7931A] mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Processing Payment...
            </h1>
            <p className="text-zinc-400">
              Please wait while we confirm your purchase.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
            >
              <img 
                src={MASCOT_IMAGE} 
                alt="Bitcoin Brain" 
                className="w-32 h-32 mx-auto rounded-2xl mb-4 btc-glow-lg"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                Welcome to <span className="text-[#F7931A]">Bitcoin Brain!</span>
              </h1>
              {tierName && (
                <p className="text-[#F7931A] font-medium text-sm mb-2 capitalize">
                  {tierName.replace("_", " ")} Access Activated
                </p>
              )}
              <p className="text-zinc-400 mb-8">
                Your full course is unlocked. 21 sections, Quiz Arena, Bitcoin Simulator, and Weekly Intelligence Briefs are all yours.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="grid grid-cols-3 gap-3 mb-8">
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3">
                <BookOpen size={20} className="text-[#F7931A] mx-auto mb-1" />
                <p className="text-xs text-zinc-400">21 Sections</p>
              </div>
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3">
                <Trophy size={20} className="text-[#F7931A] mx-auto mb-1" />
                <p className="text-xs text-zinc-400">Quiz Arena</p>
              </div>
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-3">
                <Zap size={20} className="text-[#F7931A] mx-auto mb-1" />
                <p className="text-xs text-zinc-400">Weekly Briefs</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate("/course/section/1")}
                className="btn-primary text-lg px-8"
                data-testid="start-learning-btn"
              >
                <Sparkles size={18} className="mr-2" /> Start Learning
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="border-white/10 text-zinc-300 hover:bg-white/5"
                data-testid="go-dashboard-btn"
              >
                Dashboard
              </Button>
            </motion.div>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-zinc-400 mb-8">
              We couldn't confirm your payment. Please try again or contact support at bitcoinbrain21m@gmail.com.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate("/pricing")}
                className="btn-secondary"
              >
                Back to Pricing
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="btn-primary"
              >
                Dashboard
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
